/**
 * Vista: Personas y roles (ERP F1, B2 · diseño §3, D5, D6)
 * Un solo catálogo de personas; cada persona tiene uno o varios roles y su ficha por rol.
 *   · Vendedor (agente de ventas) ≠ distribuidor. Un empleado nunca es distribuidor (D5).
 *   · Teléfono, correo y RFC no se repiten: el alta dice con quién choca (D6).
 * Patrón de 2 pestañas: «Consultar» (resumen, filtros y listado) y «Agregar / editar»; el lápiz abre la persona en el formulario.
 */

import personasErp from '../../services/erpPersonas.service.js';
import catalogos from '../../services/erpCatalogos.service.js';
import permisos from '../../services/permissions.service.js';
import modal from '../../utils/modal.js';
import toast from '../../utils/toast.js';
import { esc, errMsg, badge, val, numOrNull } from './erpUi.js';
import { permisosPantalla, pestanasHtml, Pestanas } from '../../utils/pestanasErp.js';

const ROLES = [
  { clave: 'cliente', nombre: 'Cliente', ayuda: 'a quien se le factura' },
  { clave: 'distribuidor', nombre: 'Distribuidor', ayuda: 'el canal: a quien AgroMX le vende' },
  { clave: 'agente_ventas', nombre: 'Vendedor (agente de ventas)', ayuda: 'cobra comisión; puede tener otros roles' },
  { clave: 'empleado', nombre: 'Empleado', ayuda: 'personal de AgroMX' },
  { clave: 'transportista', nombre: 'Transportista', ayuda: 'flete' }
];
const NOMBRE_ROL = {
  cliente: 'Cliente', distribuidor: 'Distribuidor', agente_ventas: 'Vendedor', empleado: 'Empleado', transportista: 'Transportista',
  proveedor: 'Proveedor', agente_aduanal: 'Agente aduanal', responsable: 'Responsable'
};
const COLOR_ROL = { distribuidor: 'primary', cliente: 'info', agente_ventas: 'success', empleado: 'warning', proveedor: 'secondary' };
const REGIMENES = [
  ['', '— Sin capturar —'], ['601', '601 General de Ley Personas Morales'], ['603', '603 Personas Morales con Fines no Lucrativos'],
  ['605', '605 Sueldos y Salarios'], ['606', '606 Arrendamiento'], ['612', '612 Actividades Empresariales y Profesionales'],
  ['616', '616 Sin obligaciones fiscales'], ['621', '621 Incorporación Fiscal'], ['622', '622 Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras'],
  ['625', '625 Plataformas Tecnológicas'], ['626', '626 Régimen Simplificado de Confianza']
];
const FILTROS = [
  ['', 'Todas'], ['cliente', 'Clientes'], ['distribuidor', 'Distribuidores'], ['agente_ventas', 'Vendedores'],
  ['empleado', 'Empleados'], ['proveedor', 'Proveedores'], ['sin_rol', 'Sin rol']
];

export default {
  data: { personas: [], marcas: [], agentes: [], resumen: null, edit: null, filtro: '', verBajas: false },

  async render() {
    const pp = permisosPantalla('personas_roles');
    const chk = (id, txt, ayuda) => `<label class="erp-check" title="${esc(ayuda || '')}"><input type="checkbox" id="${id}" /> ${esc(txt)}</label>`;
    const arriba = `
      <div class="erp-aviso erp-aviso-info">
        Un solo catálogo: la misma persona puede ser cliente, distribuidor y vendedor a la vez.
        El <strong>vendedor</strong> es quien vende y cobra comisión; el <strong>distribuidor</strong> es el canal al que AgroMX le vende
        (o <strong>Venta directa AgroMX</strong>). Un <strong>empleado no puede ser distribuidor</strong>.
      </div>`;

    const formulario = `
      <div class="card erp-form-card" id="prFormCard">
        <div class="card-header"><h2 class="card-title" id="prFormTitulo">Nueva persona</h2><span id="prFormCodigo" class="text-muted"></span></div>
        <form id="prForm" autocomplete="off">
          <div class="form-row">
            <div class="form-group" style="max-width:180px;">
              <label>Tipo *</label>
              <select id="pr_tipo" class="form-control"><option value="fisica">Persona física</option><option value="moral">Persona moral</option></select>
            </div>
            <div class="form-group">
              <label>Nombre o razón social *</label>
              <input type="text" id="pr_nombre" class="form-control" maxlength="200" required placeholder="Ej: AGRO-PARTES PUEBLA S.A. DE C.V." />
            </div>
            <div class="form-group">
              <label>Nombre comercial</label>
              <input type="text" id="pr_comercial" class="form-control" maxlength="200" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Teléfono</label><input type="tel" id="pr_tel" class="form-control" maxlength="20" placeholder="10 dígitos" /></div>
            <div class="form-group"><label>Correo</label><input type="email" id="pr_email" class="form-control" maxlength="100" /></div>
            <div class="form-group"><label>RFC</label><input type="text" id="pr_rfc" class="form-control" maxlength="13" style="text-transform:uppercase;" placeholder="Vacío si es extranjero o público en general" /></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Ciudad</label><input type="text" id="pr_ciudad" class="form-control" maxlength="100" /></div>
            <div class="form-group"><label>Estado</label><input type="text" id="pr_estado" class="form-control" maxlength="50" /></div>
            <div class="form-group"><label>Régimen fiscal SAT</label>
              <select id="pr_regimen" class="form-control">${REGIMENES.map(([v, t]) => `<option value="${v}">${esc(t)}</option>`).join('')}</select></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>ID fiscal extranjero</label><input type="text" id="pr_idext" class="form-control" maxlength="40" placeholder="Tax ID (proveedores del extranjero)" /></div>
            <div class="form-group" style="display:flex;align-items:flex-end;">${chk('pr_grupo', 'Empresa del grupo (DMP, CES, STV…)')}</div>
          </div>
          <div id="pr_choque" class="erp-aviso erp-aviso-warn" style="display:none;"></div>

          <div class="erp-seccion">
            <h4>Roles</h4>
            <div class="erp-checks">${ROLES.map((r) => chk(`pr_rol_${r.clave}`, r.nombre, r.ayuda)).join('')}</div>
            <div id="pr_roles_fijos" class="form-help" style="margin-top:.4rem;"></div>
            <div id="pr_d5" class="erp-aviso erp-aviso-warn" style="display:none;margin-top:.5rem;">
              <strong>Un empleado no puede ser distribuidor (D5).</strong> Quita uno de los dos roles.
            </div>
            <div id="pr_nota_cliente" class="form-help" style="display:none;">Un distribuidor también queda como cliente (es a quien se le factura).</div>
          </div>

          <div class="erp-seccion" id="sec_cliente" style="display:none;">
            <h4>Ficha de cliente</h4>
            <div class="form-row">
              <div class="form-group"><label>Límite de crédito</label><input type="number" id="cl_limite" class="form-control" min="0" step="0.01" value="0" /></div>
              <div class="form-group" style="max-width:130px;"><label>Moneda</label><select id="cl_moneda" class="form-control"><option>MXN</option><option>USD</option><option>EUR</option></select></div>
              <div class="form-group" style="max-width:150px;"><label>Días de crédito</label><input type="number" id="cl_dias" class="form-control" min="0" max="180" value="0" /></div>
              <div class="form-group"><label>Vendedor asignado</label><select id="cl_agente" class="form-control"></select></div>
            </div>
            <div class="form-help">El crédito lo carga Jorge. El bloqueo por vencido y el desbloqueo con firmas llegan en el bloque 4.</div>
          </div>

          <div class="erp-seccion" id="sec_distribuidor" style="display:none;">
            <h4>Ficha de distribuidor</h4>
            <div class="form-row">
              <div class="form-group"><label>Zona</label><input type="text" id="di_zona" class="form-control" maxlength="80" placeholder="Ej: Puebla norte" /></div>
              <div class="form-group" style="max-width:220px;"><label>Día de verificación (consigna)</label><input type="number" id="di_dia" class="form-control" min="1" max="31" placeholder="1 a 31" /></div>
            </div>
            <label>Marcas que distribuye</label>
            <div class="erp-checks" id="di_marcas"></div>
          </div>

          <div class="erp-seccion" id="sec_agente_ventas" style="display:none;">
            <h4>Ficha de vendedor</h4>
            <div class="form-row">
              <div class="form-group" style="max-width:180px;"><label>Puesto</label><select id="ag_puesto" class="form-control"><option value="agente">Agente</option><option value="gerente">Gerente</option></select></div>
              <div class="form-group"><label>Gerente</label><select id="ag_gerente" class="form-control"></select></div>
              <div class="form-group"><label>Zona</label><input type="text" id="ag_zona" class="form-control" maxlength="80" /></div>
              <div class="form-group" style="display:flex;align-items:flex-end;">${chk('ag_director', 'Es «director» (sus ventas se reportan aparte)')}</div>
            </div>
            <label>Marcas a las que está ligado</label>
            <div class="erp-checks" id="ag_marcas"></div>
          </div>

          <div class="erp-seccion" id="sec_empleado" style="display:none;">
            <h4>Ficha de empleado</h4>
            <div class="form-row">
              <div class="form-group"><label>Área</label><input type="text" id="em_area" class="form-control" maxlength="60" placeholder="Ej: produccion" /></div>
              <div class="form-group"><label>Puesto</label><input type="text" id="em_puesto" class="form-control" maxlength="80" /></div>
            </div>
          </div>

          <div class="form-group" style="margin-top:var(--spacing-md);"><label>Notas</label><input type="text" id="pr_notas" class="form-control" maxlength="500" /></div>
          <div class="erp-acciones-form">
            <button type="button" class="btn btn-secondary" id="btnPrLimpiar">Cancelar</button>
            <button type="submit" class="btn btn-primary" id="btnPrGuardar">Guardar persona</button>
          </div>
        </form>
      </div>`;

    const consultar = `
      <div id="pr_resumen"></div>

      <div class="card">
        <div class="card-header" style="flex-wrap:wrap;gap:.5rem;">
          <h2 class="card-title">Personas</h2>
          <div style="display:flex;gap:.5rem;flex-wrap:wrap;align-items:center;">
            <select id="pr_filtro" class="form-control" style="max-width:190px;">${FILTROS.map(([v, t]) => `<option value="${v}">${t}</option>`).join('')}</select>
            <label class="erp-check"><input type="checkbox" id="pr_bajas" /> Ver dadas de baja</label>
            <input type="text" id="pr_buscar" class="form-control" placeholder="Buscar nombre, código, RFC, teléfono…" style="max-width:260px;" />
          </div>
        </div>
        <div class="table-container">
          <table class="table">
            <thead><tr><th>Persona</th><th>Roles</th><th class="tbl-hide-mobile">Contacto</th><th class="tbl-hide-mobile">Usuario</th><th style="text-align:right;">Acciones</th></tr></thead>
            <tbody id="prBody"><tr><td colspan="5" class="text-center"><div class="loading-spinner"></div></td></tr></tbody>
          </table>
        </div>
      </div>
    `;
    return pestanasHtml({ id: 'pers', titulo: 'Personas y roles', ...pp, arriba, consultar, formulario });
  },

  async afterRender() {
    if (!permisos.canView('personas_roles')) {
      document.getElementById('content').innerHTML = '<div style="padding:2rem;text-align:center;"><h2>Acceso denegado</h2></div>';
      return;
    }
    window.currentView = this;
    this.data.edit = null;
    this.tabs = new Pestanas('pers', { alAgregar: () => this.limpiar() });
    const q = new URLSearchParams(window.location.search);
    if (q.get('rol')) this.data.filtro = q.get('rol');
    const f = document.getElementById('pr_filtro');
    if (f) f.value = this.data.filtro;
    document.getElementById('prForm')?.addEventListener('submit', (e) => { e.preventDefault(); this.guardar(); });
    document.getElementById('btnPrLimpiar')?.addEventListener('click', () => { this.limpiar(); this.tabs.volver(); });
    ROLES.forEach((r) => document.getElementById(`pr_rol_${r.clave}`)?.addEventListener('change', () => this.pintarSecciones()));
    document.getElementById('ag_puesto')?.addEventListener('change', () => this.pintarSecciones());
    document.getElementById('pr_bajas')?.addEventListener('change', (e) => { this.data.verBajas = e.target.checked; this.cargarLista(); });
    f?.addEventListener('change', (e) => { this.data.filtro = e.target.value; this.cargarLista(); });
    let t;
    document.getElementById('pr_buscar')?.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.cargarLista(), 250); });
    await Promise.all([this.cargarCatalogos(), this.cargarLista()]);
  },

  async cargarCatalogos() {
    try {
      const [rm, ra] = await Promise.all([catalogos.marcas.listar({ activo: true }), personasErp.agentes()]);
      this.data.marcas = (rm?.data?.marcas || []).filter((m) => !m.deleted_at);
      this.data.agentes = ra?.data?.personas || [];
    } catch (err) {
      toast.error(errMsg(err, 'No se pudieron cargar las marcas y los vendedores'));
    }
    const marcasHtml = (pref) => this.data.marcas.length
      ? this.data.marcas.map((m) => `<label class="erp-check"><input type="checkbox" class="${pref}" value="${m.id}" /> ${esc(m.nombre)}</label>`).join('')
      : '<span class="text-muted">Sin marcas en el catálogo</span>';
    const di = document.getElementById('di_marcas');
    if (di) di.innerHTML = marcasHtml('di_marca');
    const ag = document.getElementById('ag_marcas');
    if (ag) ag.innerHTML = marcasHtml('ag_marca');
    this.llenarAgentes();
  },

  llenarAgentes() {
    const yo = this.data.edit?.id;
    const cl = document.getElementById('cl_agente');
    if (cl) {
      const v = cl.value;
      cl.innerHTML = '<option value="">— Sin vendedor asignado —</option>' + this.data.agentes.filter((a) => a.id !== yo)
        .map((a) => `<option value="${a.id}">${esc(a.nombre_completo)}${a.puesto === 'gerente' ? ' (gerente)' : ''}</option>`).join('');
      cl.value = v;
    }
    const ge = document.getElementById('ag_gerente');
    if (ge) {
      const v = ge.value;
      ge.innerHTML = '<option value="">— Sin gerente —</option>' + this.data.agentes.filter((a) => a.puesto === 'gerente' && a.id !== yo)
        .map((a) => `<option value="${a.id}">${esc(a.nombre_completo)}</option>`).join('');
      ge.value = v;
    }
  },

  rolesMarcados() {
    return ROLES.filter((r) => document.getElementById(`pr_rol_${r.clave}`)?.checked).map((r) => r.clave);
  },

  pintarSecciones() {
    const roles = this.rolesMarcados();
    const d5 = roles.includes('distribuidor') && roles.includes('empleado');
    const avisoD5 = document.getElementById('pr_d5');
    if (avisoD5) avisoD5.style.display = d5 ? '' : 'none';
    const btn = document.getElementById('btnPrGuardar');
    if (btn) btn.disabled = d5;
    // un distribuidor siempre es cliente
    const cli = document.getElementById('pr_rol_cliente');
    if (cli) {
      if (roles.includes('distribuidor')) { cli.checked = true; cli.disabled = true; } else { cli.disabled = false; }
    }
    const nota = document.getElementById('pr_nota_cliente');
    if (nota) nota.style.display = roles.includes('distribuidor') ? '' : 'none';
    const activos = this.rolesMarcados();
    ['cliente', 'distribuidor', 'agente_ventas', 'empleado'].forEach((r) => {
      const s = document.getElementById(`sec_${r}`);
      if (s) s.style.display = activos.includes(r) ? '' : 'none';
    });
    const ge = document.getElementById('ag_gerente');
    if (ge) ge.disabled = document.getElementById('ag_puesto')?.value === 'gerente';
  },

  async cargarLista() {
    const body = document.getElementById('prBody');
    try {
      const filtro = this.data.filtro;
      const [rl, rr] = await Promise.all([
        personasErp.listar({ rol: filtro && filtro !== 'sin_rol' ? filtro : '', sin_rol: filtro === 'sin_rol', search: val('pr_buscar'), incluir_bajas: this.data.verBajas }),
        personasErp.resumen()
      ]);
      this.data.personas = rl?.data?.personas || [];
      this.data.resumen = rr?.data || null;
      this.renderResumen();
      this.renderLista();
    } catch (err) {
      if (body) body.innerHTML = `<tr><td colspan="5" class="text-center text-muted">${esc(errMsg(err, 'No se pudo cargar'))}</td></tr>`;
    }
  },

  renderResumen() {
    const el = document.getElementById('pr_resumen');
    const r = this.data.resumen;
    if (!el || !r) return;
    const n = (k) => r.por_rol?.[k] || 0;
    el.innerHTML = `
      <div class="erp-checks" style="margin-bottom:var(--spacing-md);font-size:var(--font-size-sm);">
        <span><strong>${r.personas}</strong> personas</span>
        <span>${n('cliente')} clientes</span><span>${n('distribuidor')} distribuidores</span>
        <span>${n('agente_ventas')} vendedores</span><span>${n('empleado')} empleados</span><span>${n('proveedor')} proveedores</span>
        <span>${r.sin_rol ? `<a href="#" onclick="window.currentView.verSinRol();return false;">${r.sin_rol} sin rol</a>` : '0 sin rol'}</span>
        ${r.por_corregir ? `<a href="/catalogos/calidad-distribuidor" data-link class="badge badge-danger">${r.por_corregir} documento(s) con distribuidor por corregir</a>` : ''}
      </div>`;
  },

  verSinRol() {
    this.data.filtro = 'sin_rol';
    const f = document.getElementById('pr_filtro');
    if (f) f.value = 'sin_rol';
    this.cargarLista();
  },

  renderLista() {
    const body = document.getElementById('prBody');
    if (!body) return;
    const lista = this.data.personas;
    if (!lista.length) { body.innerHTML = '<tr><td colspan="5" class="text-center text-muted" style="padding:1.5rem;">Sin personas con ese filtro.</td></tr>'; return; }
    body.innerHTML = lista.map((p) => `
      <tr ${p.eliminado ? 'style="opacity:.55;"' : ''}>
        <td><strong>${esc(p.nombre_completo)}</strong> <code style="font-size:.7rem;">${esc(p.codigo)}</code>
          <div style="font-size:.75rem;" class="text-muted">${p.tipo_persona === 'moral' ? 'Moral' : 'Física'}${p.rfc ? ` · ${esc(p.rfc)}` : ''}${p.ciudad ? ` · ${esc(p.ciudad)}` : ''}</div>
          ${p.venta_directa ? '<span class="erp-chip erp-chip-oblig">persona fija · Venta directa</span>' : ''}
          ${p.es_empresa_grupo && !p.venta_directa ? '<span class="erp-chip">empresa del grupo</span>' : ''}
          ${p.eliminado ? badge('Dada de baja', 'danger') : ''}</td>
        <td>${p.roles.length ? p.roles.map((r) => badge(NOMBRE_ROL[r] || r, COLOR_ROL[r] || 'secondary')).join(' ') : '<span class="erp-chip" style="background:#fee2e2;color:#991b1b;">sin rol</span>'}
          ${p.unidades_distribuidor ? `<div style="font-size:.72rem;" class="text-muted">${p.unidades_distribuidor} unidad(es) como distribuidor</div>` : ''}
          ${p.marcas ? `<div style="font-size:.72rem;" class="text-muted">Marcas: ${esc(p.marcas)}</div>` : ''}
          ${p.agente_puesto && p.roles.includes('agente_ventas') ? `<div style="font-size:.72rem;" class="text-muted">${p.agente_puesto === 'gerente' ? 'Gerente' : 'Agente'}${p.agente_director ? ' · director' : ''}</div>` : ''}</td>
        <td class="tbl-hide-mobile" style="font-size:.8rem;">${p.telefono ? esc(p.telefono) : ''}${p.email ? `<div>${esc(p.email)}</div>` : ''}${!p.telefono && !p.email ? '<span class="text-muted">—</span>' : ''}</td>
        <td class="tbl-hide-mobile" style="font-size:.8rem;">${p.usuarios ? esc(p.usuarios) : '<span class="text-muted">—</span>'}</td>
        <td style="text-align:right;white-space:nowrap;">
          ${p.eliminado ? (permisos.canDelete('personas_roles') ? `<button class="btn-icon btn-success" title="Restaurar" onclick="window.currentView.restaurar(${p.id})">↩️</button>` : '') : `
            ${permisos.canEdit('personas_roles') ? `<button class="btn-icon btn-edit" title="Editar" onclick="window.currentView.editar(${p.id})">✏️</button>` : ''}
            <a class="btn-icon" title="Ficha completa (contactos, cuentas)" href="/catalogos/personas/${p.id}" data-link>📄</a>
            ${permisos.canDelete('personas_roles') && !p.venta_directa ? `<button class="btn-icon btn-danger" title="Dar de baja" onclick="window.currentView.baja(${p.id})">🗑️</button>` : ''}`}
        </td>
      </tr>`).join('');
  },

  limpiar() {
    this.data.edit = null;
    document.getElementById('prForm')?.reset();
    document.getElementById('prFormTitulo').textContent = 'Nueva persona';
    document.getElementById('prFormCodigo').textContent = '';
    document.getElementById('prFormCard')?.classList.remove('editando');
    document.getElementById('btnPrGuardar').textContent = 'Guardar persona';
    document.getElementById('pr_roles_fijos').innerHTML = '';
    document.getElementById('pr_choque').style.display = 'none';
    const nom = document.getElementById('pr_nombre');
    if (nom) nom.readOnly = false;
    const tipo = document.getElementById('pr_tipo');
    if (tipo) tipo.disabled = false;
    document.querySelectorAll('.di_marca, .ag_marca').forEach((c) => { c.checked = false; });
    ROLES.forEach((r) => { const c = document.getElementById(`pr_rol_${r.clave}`); if (c) c.disabled = false; });
    this.llenarAgentes();
    this.pintarSecciones();
  },

  async editar(id) {
    let p;
    try {
      p = (await personasErp.obtener(id))?.data;
    } catch (err) { toast.error(errMsg(err, 'No se pudo abrir la persona')); return; }
    this.limpiar();
    this.data.edit = p;
    this.llenarAgentes();
    const set = (i, v) => { const el = document.getElementById(i); if (el) el.value = v ?? ''; };
    document.getElementById('prFormTitulo').textContent = `Editar ${p.nombre_completo}`;
    document.getElementById('prFormCodigo').textContent = p.codigo;
    document.getElementById('prFormCard')?.classList.add('editando');
    document.getElementById('btnPrGuardar').textContent = 'Guardar cambios';
    set('pr_tipo', p.tipo_persona); document.getElementById('pr_tipo').disabled = true;
    set('pr_nombre', p.nombre_completo); document.getElementById('pr_nombre').readOnly = !!p.venta_directa;
    set('pr_comercial', p.nombre_comercial); set('pr_tel', p.telefono); set('pr_email', p.email); set('pr_rfc', p.rfc);
    set('pr_ciudad', p.ciudad); set('pr_estado', p.estado); set('pr_regimen', p.regimen_fiscal_sat || ''); set('pr_idext', p.id_fiscal_extranjero);
    set('pr_notas', p.notas);
    document.getElementById('pr_grupo').checked = !!p.es_empresa_grupo;
    ROLES.forEach((r) => { const c = document.getElementById(`pr_rol_${r.clave}`); if (c) c.checked = p.roles.includes(r.clave); });
    if (p.venta_directa) document.getElementById('pr_rol_distribuidor').disabled = true;
    const fijos = p.roles.filter((r) => !ROLES.some((x) => x.clave === r));
    document.getElementById('pr_roles_fijos').innerHTML = fijos.length
      ? `También es: ${fijos.map((r) => badge(NOMBRE_ROL[r] || r, COLOR_ROL[r] || 'secondary')).join(' ')} <span class="text-muted">(se administra en su pantalla de siempre)</span>` : '';
    if (p.cliente) { set('cl_limite', p.cliente.limite_credito); set('cl_moneda', p.cliente.moneda_credito); set('cl_dias', p.cliente.dias_credito); set('cl_agente', p.cliente.agente_persona_id || ''); }
    if (p.distribuidor) { set('di_zona', p.distribuidor.zona); set('di_dia', p.distribuidor.verificacion_dia_mes); }
    if (p.agente) { set('ag_puesto', p.agente.puesto); set('ag_gerente', p.agente.gerente_persona_id || ''); set('ag_zona', p.agente.zona); document.getElementById('ag_director').checked = !!p.agente.es_director; }
    if (p.empleado) { set('em_area', p.empleado.area); set('em_puesto', p.empleado.puesto); }
    document.querySelectorAll('.di_marca').forEach((c) => { c.checked = p.marcas_distribuidor.includes(Number(c.value)); });
    document.querySelectorAll('.ag_marca').forEach((c) => { c.checked = p.marcas_agente.includes(Number(c.value)); });
    this.pintarSecciones();
    this.tabs.abrirFormulario({ editando: true, etiqueta: p.codigo });
  },

  armar() {
    const roles = this.rolesMarcados();
    const marcas = (cls) => [...document.querySelectorAll(`.${cls}:checked`)].map((c) => Number(c.value));
    const body = {
      nombre_completo: val('pr_nombre'),
      nombre_comercial: val('pr_comercial') || null,
      telefono: val('pr_tel') || null,
      email: val('pr_email') || null,
      rfc: val('pr_rfc') || null,
      ciudad: val('pr_ciudad') || null,
      estado: val('pr_estado') || null,
      regimen_fiscal_sat: val('pr_regimen') || null,
      id_fiscal_extranjero: val('pr_idext') || null,
      es_empresa_grupo: !!document.getElementById('pr_grupo')?.checked,
      notas: val('pr_notas') || null,
      roles
    };
    if (roles.includes('cliente')) body.cliente = { limite_credito: numOrNull(val('cl_limite')) || 0, moneda_credito: val('cl_moneda') || 'MXN', dias_credito: numOrNull(val('cl_dias')) || 0, agente_persona_id: numOrNull(val('cl_agente')) };
    if (roles.includes('distribuidor')) { body.distribuidor = { zona: val('di_zona') || null, verificacion_dia_mes: numOrNull(val('di_dia')) }; body.marcas_distribuidor = marcas('di_marca'); }
    if (roles.includes('agente_ventas')) {
      const puesto = val('ag_puesto') || 'agente';
      body.agente = { puesto, gerente_persona_id: puesto === 'gerente' ? null : numOrNull(val('ag_gerente')), zona: val('ag_zona') || null, es_director: !!document.getElementById('ag_director')?.checked };
      body.marcas_agente = marcas('ag_marca');
    }
    if (roles.includes('empleado')) body.empleado = { area: val('em_area') || null, puesto: val('em_puesto') || null };
    return body;
  },

  async guardar() {
    const body = this.armar();
    const choque = document.getElementById('pr_choque');
    choque.style.display = 'none';
    if (!body.nombre_completo) { toast.warning('Escribe el nombre'); return; }
    if (body.roles.includes('distribuidor') && body.roles.includes('empleado')) { toast.error('Un empleado no puede ser distribuidor (D5)'); return; }
    try {
      if (this.data.edit) {
        if (this.data.edit.venta_directa) delete body.nombre_completo;
        const r = await personasErp.actualizar(this.data.edit.id, body);
        toast.success(`${r?.data?.codigo || ''} actualizada`);
      } else {
        body.tipo_persona = val('pr_tipo') || 'fisica';
        const r = await personasErp.crear(body);
        toast.success(`Persona ${r?.data?.codigo || ''} creada`);
      }
      this.limpiar();
      this.tabs.volver();
      await Promise.all([this.cargarLista(), this.cargarCatalogos()]);
    } catch (err) {
      const msg = err?.message && !Array.isArray(err.details) ? err.message : errMsg(err);
      if (err?.status === 409 || /ya es de/.test(msg)) {
        choque.innerHTML = `<strong>No se guardó:</strong> ${esc(msg)}.`;
        choque.style.display = '';
      }
      toast.error(msg);
    }
  },

  baja(id) {
    const p = this.data.personas.find((x) => x.id === id);
    modal.confirm('Dar de baja la persona', `<p>¿Dar de baja <strong>${esc(p.nombre_completo)}</strong> (${esc(p.codigo)})?</p><p class="text-muted">No se borra: se restaura con «Ver dadas de baja».</p>`, async () => {
      try { await personasErp.eliminar(id); toast.success('Persona dada de baja'); await this.cargarLista(); } catch (err) { toast.error(errMsg(err)); }
    });
  },

  async restaurar(id) {
    try { await personasErp.restaurar(id); toast.success('Persona restaurada'); await this.cargarLista(); } catch (err) { toast.error(errMsg(err)); }
  }
};
